/**
 * Dexie-backed OCPP configuration for the current station.
 *
 * Two storage backends, one flat read interface:
 *   - v1.6 stations use the flat `configurations` table (key/value).
 *   - v2.0.1 stations use the structured `variableValues` Device Model
 *     table (Session 19b). The store PROJECTS the (Actual, unqualified)
 *     rows down to the same flat `"Component.Variable"` ConfigurationKey
 *     shape, so every existing flat-key consumer (HeartbeatInterval
 *     timer, measurands, AuthorizationCacheEnabled, …) keeps working.
 *
 * The structured handlers (GetVariables / SetVariables / GetReport) read
 * the `variableValues` table directly for attributeType + characteristics;
 * the store is the flat seam for everyone else. `refresh` stashes the
 * station's version so value-mutating actions route to the right backend.
 */
import { create } from 'zustand';
import { db } from '@/persistence/dexie/db';
import { isV2x } from '@/lib/ocppProfile';
import {
  ConfigurationRepositoryDexie,
  VariableValuesRepositoryDexie,
} from '@/persistence/dexie/repositories';
import {
  connectorCountFor,
  numberOfConnectorsKey,
  type EvseLike,
} from '@/lib/connectorCount';
import { DEFAULT_CONFIGURATION_KEYS_V16 } from '@/protocol/v16/OcppV16Adapter';
import { seedSnapshots, lookupDef } from '@/protocol/v201/deviceModel';
import { splitComponentVariable } from '@/protocol/v201/actions/notifyReport';
import type { ConfigurationKeySnapshot } from '@/domain';
import type { ConfigurationKeyDescriptor } from '@/protocol/OcppAdapter';
import type { VariableValueSnapshot } from '@/persistence/ports';
import {
  ConfigurationKeyType,
  ConfigurationSection,
  type OcppVersion,
  type StationId,
} from '@/domain/types';

export interface ConfigurationStoreState {
  readonly entries: readonly ConfigurationKeySnapshot[];
  readonly status: 'idle' | 'loading' | 'ready';
}

export interface ConfigurationStoreActions {
  refresh(
    stationId: StationId,
    ocppVersion?: OcppVersion,
    evses?: readonly EvseLike[],
  ): Promise<void>;
  setValue(stationId: StationId, key: string, value: string): Promise<boolean>;
  resetToDefault(stationId: StationId, key: string): Promise<boolean>;
  deleteKey(stationId: StationId, key: string): Promise<boolean>;
  addCustom(stationId: StationId, snap: ConfigurationKeySnapshot): Promise<boolean>;
  applyDerivedFromTopology(
    stationId: StationId,
    ocppVersion: OcppVersion,
    evses: readonly EvseLike[],
  ): Promise<void>;
}

export type ConfigurationStore = ConfigurationStoreState & ConfigurationStoreActions;

const repo = new ConfigurationRepositoryDexie(db);
const varRepo = new VariableValuesRepositoryDexie(db);

function defaultByKey(
  list: readonly ConfigurationKeyDescriptor[],
): ReadonlyMap<string, ConfigurationKeyDescriptor> {
  const m = new Map<string, ConfigurationKeyDescriptor>();
  for (const d of list) m.set(d.key, d);
  return m;
}

const DEFAULTS_V16 = defaultByKey(DEFAULT_CONFIGURATION_KEYS_V16);

// Session 19b — the store remembers which backend the current station
// uses so value-mutating actions (which don't receive ocppVersion) route
// correctly. Module-scoped because it mirrors a single active station per
// tab; reset on every refresh().
let currentVersion: OcppVersion = '1.6J';

function isV201(): boolean {
  // 2.1 uses the same Device Model backend as 2.0.1 (backward-compatible superset).
  return isV2x(currentVersion);
}

/** v2.0.1 DataEnumType → the flat ConfigurationKeyType used by the UI. */
function dataTypeToConfigType(dt: string): ConfigurationKeyType {
  switch (dt) {
    case 'integer':
      return ConfigurationKeyType.Integer;
    case 'boolean':
      return ConfigurationKeyType.Boolean;
    case 'MemberList':
    case 'OptionList':
    case 'SequenceList':
      return ConfigurationKeyType.CSL;
    default:
      return ConfigurationKeyType.String;
  }
}

/** Project an (Actual, unqualified) Device Model row to the flat key shape. */
function projectVar(v: VariableValueSnapshot): ConfigurationKeySnapshot {
  return {
    key: `${v.component}.${v.variable}`,
    value: v.value,
    readonly: v.mutability === 'ReadOnly',
    type: dataTypeToConfigType(v.dataType),
    section: (v.section as ConfigurationSection) ?? ConfigurationSection.Custom,
  };
}

/** Read the current flat-projected entries from whichever backend is active. */
async function readEntries(stationId: StationId): Promise<readonly ConfigurationKeySnapshot[]> {
  if (isV201()) {
    const rows = await varRepo.byStation(stationId);
    return rows
      .filter((r) => r.attributeType === 'Actual' && r.evseId === 0 && r.connectorId === 0)
      .map(projectVar);
  }
  return repo.byStation(stationId);
}

export const useConfigurationStore = create<ConfigurationStore>((set, get) => ({
  entries: [],
  status: 'idle',

  async refresh(
    stationId: StationId,
    ocppVersion?: OcppVersion,
    evses?: readonly EvseLike[],
  ): Promise<void> {
    set({ status: 'loading' });
    if (ocppVersion !== undefined) currentVersion = ocppVersion;

    if (isV201()) {
      // Seed the Device Model on first visit (idempotent — only missing
      // rows are written).
      const existing = await varRepo.byStation(stationId);
      if (existing.length === 0) {
        await varRepo.seedMissing(stationId, seedSnapshots());
      }
    } else {
      // Seed missing default keys (idempotent — mirrors the v201
      // device-model seedMissing path). On first visit this writes the full
      // set; for stations created before a new default key was added (e.g.
      // LocalPreAuthorize) it backfills only the absent ones, leaving any
      // user-modified values untouched. Standard keys can't be user-deleted
      // (only Custom keys have a Delete affordance), so backfilling never
      // fights a deliberate removal.
      const existing = await repo.byStation(stationId);
      const present = new Set(existing.map((e) => e.key));
      for (const d of DEFAULT_CONFIGURATION_KEYS_V16) {
        if (present.has(d.key)) continue;
        await repo.upsert(stationId, {
          key: d.key,
          value: d.value,
          readonly: d.readonly,
          type: d.type,
          section: d.section,
        });
      }
    }

    if (ocppVersion !== undefined && evses !== undefined) {
      await applyDerivedToBackend(stationId, ocppVersion, evses);
    }
    set({ entries: await readEntries(stationId), status: 'ready' });
  },

  async setValue(stationId: StationId, key: string, value: string): Promise<boolean> {
    const existing = get().entries.find((e) => e.key === key);
    if (!existing) return false;
    if (existing.readonly) return false;
    if (isV201()) {
      const { component, variable } = splitComponentVariable(key);
      const rows = await varRepo.byStation(stationId);
      const row = rows.find(
        (r) =>
          r.component === component &&
          r.variable === variable &&
          r.attributeType === 'Actual' &&
          r.evseId === 0 &&
          r.connectorId === 0,
      );
      if (!row) return false;
      await varRepo.upsert(stationId, { ...row, value });
    } else {
      await repo.upsert(stationId, { ...existing, value });
    }
    set({ entries: await readEntries(stationId) });
    return true;
  },

  async resetToDefault(stationId: StationId, key: string): Promise<boolean> {
    if (isV201()) {
      const { component, variable } = splitComponentVariable(key);
      const def = lookupDef(component, variable);
      if (!def) return false;
      await varRepo.upsert(stationId, {
        component: def.component,
        evseId: def.evseId,
        connectorId: def.connectorId,
        variable: def.variable,
        attributeType: def.attributeType,
        value: def.value,
        mutability: def.mutability,
        dataType: def.dataType,
        ...(def.unit !== undefined ? { unit: def.unit } : {}),
        section: def.section,
      });
      if (key === 'DeviceDataCtrlr.NumberOfConnectors') {
        const stationModule = await import('./useStationStore');
        await applyDerivedToBackend(
          stationId,
          '2.0.1',
          stationModule.useStationStore.getState().evses,
        );
      }
      set({ entries: await readEntries(stationId) });
      return true;
    }
    const d = DEFAULTS_V16.get(key);
    if (!d) return false;
    await repo.upsert(stationId, {
      key: d.key,
      value: d.value,
      readonly: d.readonly,
      type: d.type,
      section: d.section,
    });
    if (key === 'NumberOfConnectors') {
      const stationModule = await import('./useStationStore');
      await applyDerivedToBackend(
        stationId,
        '1.6J',
        stationModule.useStationStore.getState().evses,
      );
    }
    set({ entries: await readEntries(stationId) });
    return true;
  },

  async deleteKey(stationId: StationId, key: string): Promise<boolean> {
    const existing = get().entries.find((e) => e.key === key);
    if (!existing) return false;
    if (existing.section !== ConfigurationSection.Custom) return false;
    if (isV201()) {
      const { component, variable } = splitComponentVariable(key);
      // Delete by overwriting with a tombstone is awkward; the flat
      // Custom-key delete maps to clearing that single Device Model row.
      // Re-seed-safe: a deleted Custom var simply won't reappear.
      const rows = await varRepo.byStation(stationId);
      const row = rows.find((r) => r.component === component && r.variable === variable);
      if (row) await db.variableValues.delete([stationId, row.component, row.evseId, row.connectorId, row.variable, row.attributeType]);
    } else {
      await repo.delete(stationId, key);
    }
    set({ entries: await readEntries(stationId) });
    return true;
  },

  async addCustom(stationId: StationId, snap: ConfigurationKeySnapshot): Promise<boolean> {
    if (snap.section !== ConfigurationSection.Custom) return false;
    if (get().entries.some((e) => e.key === snap.key)) return false;
    if (isV201()) {
      const { component, variable } = splitComponentVariable(snap.key);
      await varRepo.upsert(stationId, {
        component,
        variable,
        evseId: 0,
        connectorId: 0,
        attributeType: 'Actual',
        value: snap.value,
        mutability: snap.readonly ? 'ReadOnly' : 'ReadWrite',
        dataType: configTypeToDataType(snap.type),
        section: ConfigurationSection.Custom,
      });
    } else {
      await repo.upsert(stationId, snap);
    }
    set({ entries: await readEntries(stationId) });
    return true;
  },

  async applyDerivedFromTopology(
    stationId: StationId,
    ocppVersion: OcppVersion,
    evses: readonly EvseLike[],
  ): Promise<void> {
    currentVersion = ocppVersion;
    await applyDerivedToBackend(stationId, ocppVersion, evses);
    set({ entries: await readEntries(stationId) });
  },
}));

/** Flat ConfigurationKeyType → v2.0.1 DataEnumType (inverse of dataTypeToConfigType). */
function configTypeToDataType(t: ConfigurationKeyType): string {
  switch (t) {
    case ConfigurationKeyType.Integer:
      return 'integer';
    case ConfigurationKeyType.Boolean:
      return 'boolean';
    case ConfigurationKeyType.CSL:
      return 'MemberList';
    default:
      return 'string';
  }
}

/**
 * Session 18/19b — write the derived `NumberOfConnectors` value into
 * whichever backend the station uses, bypassing the readonly check.
 */
async function applyDerivedToBackend(
  stationId: StationId,
  ocppVersion: OcppVersion,
  evses: readonly EvseLike[],
): Promise<void> {
  const liveValue = String(connectorCountFor(evses));
  if (ocppVersion === '2.0.1') {
    const rows = await varRepo.byStation(stationId);
    const row = rows.find(
      (r) => r.component === 'DeviceDataCtrlr' && r.variable === 'NumberOfConnectors',
    );
    if (!row || row.value === liveValue) return;
    await varRepo.upsert(stationId, { ...row, value: liveValue });
    return;
  }
  const key = numberOfConnectorsKey(ocppVersion);
  const existing = await repo.get(stationId, key);
  if (!existing || existing.value === liveValue) return;
  await repo.upsert(stationId, { ...existing, value: liveValue });
}
